/** @jsxImportSource @emotion/react */

import {
  Icon,
  useTheme,
  Loader,
  Option,
  UtilitySelect,
  DateInput,
  Card,
} from '@quickbit/qb-design-system';
import { useEffect, useRef, useState } from 'react';
import Confetti from 'react-confetti';
import { useAsyncFn, useWindowSize } from 'react-use';
import { MerchantSelect } from 'components';
import { PlatformSelect } from 'components/PlatformSelect';
import { useInterpolate } from 'hooks';
import { getOrderCountryData, getTickingOrderData } from './api';
import { OrderTicker } from './components';
import { Map } from './components/Map';

const FETCH_INTERVAL_IN_SECONDS = 30;
const INTERPOLATION_TIME_IN_SECONDS = 60;
const CONFETTI_TIME = 10;
const ORDER_COUNT_CONFETTI_THRESHOLD = 10000;
const TRANSACTION_VOLUME_CONFETTI_THRESHOLD = 1000000;
const REVENUE_CONFETTI_THRESHOLD = 100000;
const MAX_MONTHS = 3;

const ORDER_TYPES_FILTER_OPTIONS = ['PAY', 'ONRAMP', 'PROCESSING'].map((s) => ({
  value: s,
  label: s,
}));

const getYesterday = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date;
};
const getMinDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - MAX_MONTHS);
  return date;
};

export const OrderStatistics = () => {
  const { width, height } = useWindowSize();
  const { spacing, palette } = useTheme();
  const [selectedMerchants, setSelectedMerchants] = useState<Option[]>([]);
  const [selectedPlatform, setSelectedPlatform] = useState<Option | null>(null);
  const [orderTypesFilter, setOrderTypesFilter] = useState<Option[] | null>([
    ORDER_TYPES_FILTER_OPTIONS[0],
    ORDER_TYPES_FILTER_OPTIONS[1],
  ]);

  const [dateFilterRange, setDateFilterRange] = useState<
    [Date | null, Date | null]
  >([getYesterday(), null]);

  const [showConfetti, setShowConfetti] = useState(false);
  const [recycleConfetti, setRecycleConfetti] = useState(false);

  // Order count
  const [previousOrderCount, setPreviousOrderCount] = useState(0);
  const [currentOrderCount, setCurrentOrderCount] = useState(0);
  const [orderCount, setOrderCount] = useInterpolate(
    previousOrderCount,
    currentOrderCount,
    INTERPOLATION_TIME_IN_SECONDS * 1000
  );

  // Transaction volume
  const [previousTransactionVolume, setPreviousTransactionVolume] = useState(0);
  const [currentTransactionVolume, setCurrentTransactionVolume] = useState(0);
  const [transactionVolume, setTransactionVolume] = useInterpolate(
    previousTransactionVolume,
    currentTransactionVolume,
    INTERPOLATION_TIME_IN_SECONDS * 1000
  );

  // Revenue
  const [previousRevenue, setPreviousRevenue] = useState(0);
  const [currentRevenue, setCurrentRevenue] = useState(0);
  const [revenue, setRevenue] = useInterpolate(
    previousRevenue,
    currentRevenue,
    INTERPOLATION_TIME_IN_SECONDS * 1000
  );

  // Acceptance rate
  const [previousAcceptanceRate, setPreviousAcceptanceRate] = useState(0);
  const [currentAcceptanceRate, setCurrentAcceptanceRate] = useState(0);
  const [acceptanceRate, setAcceptanceRate] = useInterpolate(
    previousAcceptanceRate,
    currentAcceptanceRate,
    INTERPOLATION_TIME_IN_SECONDS * 1000
  );

  const [
    { value: initialData, loading: initialLoading },
    initializeTickingData,
  ] = useAsyncFn(async () => {
    const data = await getTickingOrderData({
      filters: {
        createdFrom: dateFilterRange[0],
        createdTo: dateFilterRange[1],
        orderTypes: orderTypesFilter
          ? orderTypesFilter.map((v) => v.value)
          : null,
        merchantIds: selectedMerchants.map((merchant) => merchant.value),
      },
    });

    setOrderCount(data.orderCount.current);
    setTransactionVolume(data.transactionVolume.current);
    setRevenue(data.revenue.current);
    setAcceptanceRate(data.acceptanceRate.current);

    setCurrentOrderCount(data.orderCount.current);
    setCurrentTransactionVolume(data.transactionVolume.current);
    setCurrentRevenue(data.revenue.current);
    setCurrentAcceptanceRate(data.acceptanceRate.current);
    return true;
  }, [selectedMerchants, dateFilterRange, orderTypesFilter]);

  const [{ loading: tickingDataLoading }, getTickingData] =
    useAsyncFn(async () => {
      const data = await getTickingOrderData({
        filters: {
          createdFrom: dateFilterRange[0],
          createdTo: dateFilterRange[1],
          orderTypes: orderTypesFilter
            ? orderTypesFilter.map((v) => v.value)
            : null,
          merchantIds: selectedMerchants.map((merchant) => merchant.value),
        },
      });

      setPreviousOrderCount(orderCount);
      setPreviousTransactionVolume(transactionVolume);
      setPreviousRevenue(revenue);
      setPreviousAcceptanceRate(acceptanceRate);

      setCurrentOrderCount(data.orderCount.current);
      setCurrentTransactionVolume(data.transactionVolume.current);
      setCurrentRevenue(data.revenue.current);
      setCurrentAcceptanceRate(data.acceptanceRate.current);
    }, [
      orderCount,
      transactionVolume,
      revenue,
      selectedMerchants,
      dateFilterRange,
      orderTypesFilter,
    ]);

  const [{ value: countryData, loading: countryDataLoading }, getCountryData] =
    useAsyncFn(async () => {
      const data = await getOrderCountryData({
        filters: {
          createdFrom: dateFilterRange[0],
          createdTo: dateFilterRange[1],
          orderTypes: orderTypesFilter
            ? orderTypesFilter.map((v) => v.value)
            : null,
          merchantIds: selectedMerchants.map((merchant) => merchant.value),
        },
      });
      return data.countryData;
    }, [selectedMerchants, dateFilterRange, orderTypesFilter]);

  const getDataRef = useRef(getTickingData);
  // eslint-disable-next-line @typescript-eslint/require-await
  getDataRef.current = async () => {
    void getTickingData();
    void getCountryData();
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      void getDataRef.current();
    }, FETCH_INTERVAL_IN_SECONDS * 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    void initializeTickingData();
  }, [initializeTickingData]);

  useEffect(() => {
    void getCountryData();
  }, [getCountryData]);

  useEffect(() => {
    if (
      !showConfetti &&
      orderCount > 10 && // Prevent confetti from showing on initial load
      transactionVolume > 10 &&
      revenue > 10 &&
      (orderCount % ORDER_COUNT_CONFETTI_THRESHOLD <= 10 ||
        transactionVolume % TRANSACTION_VOLUME_CONFETTI_THRESHOLD <= 10 ||
        revenue % REVENUE_CONFETTI_THRESHOLD <= 10)
    ) {
      setShowConfetti(true);
      setRecycleConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
      }, CONFETTI_TIME * 1000);
      setTimeout(() => {
        setRecycleConfetti(false);
      }, (CONFETTI_TIME / 2) * 1000);
    }
  }, [showConfetti, orderCount, transactionVolume, revenue]);
  function getOrderTypeFilterLabel(orderTypesFilter: Option[]) {
    return orderTypesFilter
      .map((orderTypes) => orderTypes.label)
      .join(', ')
      .replace(/,([^,]*)$/, ' and$1');
  }

  const loading = initialLoading || tickingDataLoading || countryDataLoading;

  if (initialLoading && !initialData) {
    return (
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div
        css={{
          display: 'grid',
          gridTemplateRows: '1fr',
          gap: spacing.xxxs,
          height: '100%',
          position: 'relative',
        }}
      >
        <div
          css={{
            position: 'absolute',
            top: spacing.s,
            right: spacing.s,
            display: 'flex',
            gap: spacing.xxxs,
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          {loading && (
            <Icon
              name="spinner"
              className={{ svg: { color: palette.grey.default } }}
            />
          )}
          <MerchantSelect
            merchant={selectedMerchants}
            setMerchant={(m) => {
              setSelectedMerchants(m as Option[]);
            }}
            multiple={true}
          />
          <PlatformSelect
            platform={selectedPlatform}
            setPlatform={(m) => {
              setSelectedPlatform(m);
              if (m?.merchants) {
                setSelectedMerchants(m.merchants);
              }
            }}
          />
          <UtilitySelect
            label={
              orderTypesFilter && orderTypesFilter.length > 0
                ? getOrderTypeFilterLabel(orderTypesFilter)
                : 'Order Type'
            }
            listTitle="Order Type"
            options={ORDER_TYPES_FILTER_OPTIONS}
            value={orderTypesFilter}
            onSelect={(data) => {
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              setOrderTypesFilter((data as Option[]) || []);
            }}
            multiple={true}
          />
          <DateInput
            placeholder="Last Day"
            type="range"
            value={dateFilterRange}
            onChange={(dateRange: [Date | null, Date | null]) => {
              // set daterange value to yesterday if first index of daterange is empty
              if (!dateRange[0]) {
                dateRange[0] = getYesterday();
              }
              setDateFilterRange(dateRange);
            }}
            withTime
            minDate={getMinDate()}
          />
        </div>

        <Card
          css={{
            position: 'absolute',
            bottom: 135,
            left: spacing.s,
            zIndex: 1,
            display: 'flex',
            backgroundColor: palette.primary.x50,
            padding: spacing.xxxs,
          }}
        >
          <Icon name="info" />
          <span style={{ marginLeft: spacing.xxxs }}>
            Acceptance Rate does not include processing orders
          </span>
        </Card>

        <div
          css={{
            position: 'absolute',
            bottom: spacing.s,
            left: spacing.s,
            zIndex: 1,
            display: 'grid',
            gridTemplateColumns: 'auto auto auto auto',
            gap: spacing.xs,
          }}
        >
          <OrderTicker
            title="Acceptance rate"
            value={acceptanceRate * 100}
            isPercentage={true}
          />
          <OrderTicker
            title="Completed orders"
            value={orderCount}
            isCurrency={false}
          />
          <OrderTicker
            title="Transaction Volume"
            value={transactionVolume}
            isCurrency={true}
          />
          <OrderTicker title="Revenue" value={revenue} isCurrency={true} />
        </div>

        <Map countryData={countryData ?? []} />
      </div>
      {showConfetti && (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <Confetti
          width={width}
          height={height}
          recycle={recycleConfetti}
          numberOfPieces={1000}
        />
      )}
    </>
  );
};
